import { Dispatch } from 'react';
import { TabsEffectArgs } from '../PostEditorGroupDataEffects';
import { getApolloError } from '../../../../constants/apollo-errors';
import { ApolloErrorMapKeysType } from '../../../../models/apollo-errors';
import { GetAiCaptionsAction, InitiateCaptionsAction, PostEditorGroupDataActions } from '../PostEditorGroupDataActions';
import { addMilliseconds, isBefore, parse } from 'date-fns';
import { DateTimeStringFormatWithZone } from '../../../../constants/time-formats';
import { AiCaptionStatus } from '../../../../models/graphql';
import { GENERATING_CAPTIONS_PLACEHOLDER } from '../initial';
import { ApolloError } from '@apollo/client';
import { getApolloErrorMessage, remapApolloErrors } from '../../../../helpers/remapApolloErrors';

const MAX_ERROR_RETRIES = 3;
const MAX_CAPTIONS_EXECUTION = 30000;
const POLLING_INTERVAL = 1000;
const SINGLE_REQUEST_TIMEOUT = 10000;

export const STOPPED_CAPTIONS_PLACEHOLDER = 'Caption generation stopped.';

const captionsErrorHandler = (
  currentResults: string,
  dispatch: Dispatch<PostEditorGroupDataActions>,
  error: JSX.Element | JSX.Element[] | string
) => {
  dispatch({ type: 'setError', payload: error });
  const payload = currentResults === GENERATING_CAPTIONS_PLACEHOLDER ? STOPPED_CAPTIONS_PLACEHOLDER : currentResults;
  dispatch({ type: 'setAiResults', payload });
};

export const initiateCaptionsEffect: TabsEffectArgs = (state, effect, dispatch) => {
  const { prompt, networks, organizationId, onPlanLimitError }: InitiateCaptionsAction['payload'] = effect.payload;
  // async iife
  (async () => {
    try {
      const { data, errors } = await state.aiCaptionWriter.initiateCaptionsMutation({
        variables: { organizationId, input: { prompt, networks } }
      });
      if (data) {
        if (data.initiateCaptions.cacheKey) {
          if (state.aiCaptionWriter.forceStopped) {
            state.aiCaptionWriter.forceStopped.current = false;
          }
          dispatch({ type: 'setAiCacheKey', payload: data.initiateCaptions.cacheKey });
          dispatch({
            type: 'getCaptions',
            payload: { cacheKey: data.initiateCaptions.cacheKey, errorsCount: 0, onPlanLimitError }
          });
        }
      } else if (errors) {
        const payload = errors
          .map(error => getApolloError(error.message as ApolloErrorMapKeysType, 'initiateCaptions'))
          .join('\n');
        dispatch({ type: 'setError', payload });
      }
    } catch (e) {
      const error: ApolloError = e as unknown as ApolloError;
      if (getApolloErrorMessage(error) === 'PLAN_LIMIT_EXCEEDED') {
        onPlanLimitError();
        dispatch({ type: 'setAiLoading', payload: false });
        return;
      }
      const payload = remapApolloErrors(error);
      dispatch({ type: 'setError', payload });
    }
  })();
};

export const getCaptionsEffect: TabsEffectArgs = (state, effect, dispatch) => {
  // async iife - since we need an async fnc to await for the results
  (async () => {
    const { errorsCount, cacheKey, onPlanLimitError }: GetAiCaptionsAction['payload'] = effect.payload;
    const { getCaptionsQuery, captionResults, forceStopped } = state.aiCaptionWriter;
    try {
      if (!cacheKey) {
        const payload = getApolloError('AI_CAPTION_ERROR', 'initiateCaptions');
        captionsErrorHandler(captionResults, dispatch, payload);
        return;
      }

      const { data, error } = await getCaptionsQuery({
        variables: { cacheKey },
        fetchPolicy: 'network-only',
        context: { timeout: SINGLE_REQUEST_TIMEOUT }
      });

      if (data) {
        const { status, createdAt, captions } = data.captions;
        const stringifiedCaptions = captions.join('\n\n');
        const parsedCreatedAt = parse(createdAt + 'Z', DateTimeStringFormatWithZone, new Date());

        if (status === AiCaptionStatus.SUCCESS && forceStopped?.current === false) {
          dispatch({ type: 'setAiResults', payload: stringifiedCaptions });
        } else {
          if (forceStopped?.current === true) {
            return;
          }
          const payload = stringifiedCaptions || captionResults;
          dispatch({ type: 'setAiResults', payload });
        }
        if ([AiCaptionStatus.SUCCESS, AiCaptionStatus.STOPPED].includes(status)) {
          dispatch({ type: 'setAiLoading', payload: false });
        } else if ([AiCaptionStatus.PENDING, AiCaptionStatus.WORKING]) {
          if (isBefore(new Date(), addMilliseconds(parsedCreatedAt, MAX_CAPTIONS_EXECUTION))) {
            setTimeout(() => {
              if (forceStopped?.current === true) {
                return;
              }
              dispatch({ type: 'getCaptions', payload: { cacheKey, errorsCount: 0, onPlanLimitError } });
            }, POLLING_INTERVAL);
          } else {
            // Pending time exceeded
            const payload = getApolloError('AI_CAPTION_TIME_LIMIT_EXCEEDED', 'captions');
            captionsErrorHandler(captionResults, dispatch, payload);
          }
        } else {
          const payload = getApolloError('AI_CAPTION_ERROR', 'captions');
          captionsErrorHandler(captionResults, dispatch, payload);
        }
      } else if (error) {
        if (errorsCount < MAX_ERROR_RETRIES) {
          setTimeout(
            () =>
              dispatch({ type: 'getCaptions', payload: { cacheKey, errorsCount: errorsCount + 1, onPlanLimitError } }),
            POLLING_INTERVAL
          );
        } else {
          if (getApolloErrorMessage(error) === 'PLAN_LIMIT_EXCEEDED') {
            onPlanLimitError();
            dispatch({ type: 'setAiLoading', payload: false });
            return;
          }
          const payload = remapApolloErrors(error);
          captionsErrorHandler(captionResults, dispatch, payload);
        }
      }
    } catch (e) {
      const error: ApolloError = e as unknown as ApolloError;
      if (getApolloErrorMessage(error) === 'PLAN_LIMIT_EXCEEDED') {
        onPlanLimitError();
        dispatch({ type: 'setAiLoading', payload: false });
        return;
      }
      const payload = remapApolloErrors(error);
      captionsErrorHandler(state.aiCaptionWriter.captionResults, dispatch, payload);
    }
  })();
};

export const stopCaptionsEffect: TabsEffectArgs = (state, _effect, dispatch) => {
  // async iife since we need an async fnc to await for the results
  (async () => {
    try {
      const { cacheKey, stopCaptionsMutation, captionResults, forceStopped } = state.aiCaptionWriter;
      if (forceStopped) {
        forceStopped.current = true;
      }

      if (!cacheKey) throw new Error('Captions Cache key not set');
      dispatch({ type: 'setAiLoading', payload: false });
      if (captionResults === GENERATING_CAPTIONS_PLACEHOLDER) {
        dispatch({ type: 'setAiResults', payload: STOPPED_CAPTIONS_PLACEHOLDER });
      }
      const { errors } = await stopCaptionsMutation({
        variables: { cacheKey }
      });
      if (errors) {
        const payload = errors
          .map(error => getApolloError(error.message as ApolloErrorMapKeysType, 'captions'))
          .join('\n');
        captionsErrorHandler(captionResults, dispatch, payload);
      }
    } catch (e) {
      const error: ApolloError = e as unknown as ApolloError;
      const payload = remapApolloErrors(error);
      captionsErrorHandler(state.aiCaptionWriter.captionResults, dispatch, payload);
    }
  })();
};
